import { z } from "zod";
import { RichText } from "../RichText";
import { ctaColorEnum, ctaClasses } from "../ctaColors";
import { globalSectionSchema, subHeadingLevel } from "../globalProps";
import { isDarkBackground, CARD_SHADOW } from "../cardStyles";
import { SectionShell } from "../SectionShell";

const ctaCardSchema = z.object({
  heading: z.string().default("Card Title"),
  body: z.string().default("A brief description of this option."),
  ctaLabel: z.string().default("Learn More"),
  ctaHref: z.string().default("#"),
  icon: z.string().optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("How Can We Help?"),
  subheading: z.string().optional(),
  cards: z
    .array(ctaCardSchema)
    .default([
      {
        heading: "For Individuals",
        body: "Personal guidance tailored to your specific situation and goals.",
        ctaLabel: "Get Started",
        ctaHref: "/book",
        icon: "user",
      },
      {
        heading: "For Businesses",
        body: "Strategic advisory services to protect and grow your organization.",
        ctaLabel: "Get Started",
        ctaHref: "/book",
        icon: "building",
      },
      {
        heading: "For Teams",
        body: "Group sessions and training for your team or leadership group.",
        ctaLabel: "Get Started",
        ctaHref: "/book",
        icon: "users",
      },
    ]),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  cardStyle: z.enum(["elevated", "flat", "outlined"]).default("elevated"),
  textAlign: z.enum(["center", "left"]).default("left"),
  ctaTarget: z.enum(["_self", "_blank"]).default("_self"),
  ctaColor: ctaColorEnum.optional(),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/** Simple icon-name-to-emoji fallback (no external icon dep in common/sections) */
function CardIcon({ name }: { name: string }) {
  const map: Record<string, string> = {
    user: "\u{1F464}",
    users: "\u{1F465}",
    building: "\u{1F3E2}",
    star: "\u{2B50}",
    shield: "\u{1F6E1}\u{FE0F}",
    heart: "\u{2764}\u{FE0F}",
    briefcase: "\u{1F4BC}",
    globe: "\u{1F310}",
  };
  return (
    <span className="text-2xl" role="img" aria-hidden>
      {map[name] || name}
    </span>
  );
}

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const SubH = subHeadingLevel(safe.headingLevel);

  if (safe.cards.length === 0) return null;

  // Build card class based on cardStyle, keeping CTACards-specific rounding + padding
  const dark = isDarkBackground(safe.backgroundStyle);
  let cardBaseClass: string;
  switch (safe.cardStyle) {
    case "flat":
      cardBaseClass = dark
        ? "bg-white/10 rounded-xl p-6 @md:p-8"
        : "bg-surface/50 rounded-xl p-6 @md:p-8";
      break;
    case "outlined":
      cardBaseClass = "bg-white text-text rounded-xl p-6 @md:p-8 border border-current/10";
      break;
    case "elevated":
    default:
      cardBaseClass = `bg-white text-text rounded-xl ${CARD_SHADOW} p-6 @md:p-8`;
      break;
  }
  // flat on dark bg inherits section text color; elevated/outlined force dark text
  const needsTextText = safe.cardStyle !== "flat" || !dark;

  const colClass =
    safe.columns === 2
      ? "grid-cols-1 @md:grid-cols-2"
      : safe.columns === 4
        ? "grid-cols-1 @md:grid-cols-2 @lg:grid-cols-4"
        : "grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3";

  return (
    <SectionShell globals={safe}>
        {/* Section header */}
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80 max-w-2xl mx-auto"
              data-prop="subheading"
            />
          )}
        </div>

        {/* Cards grid */}
        <div className={`grid ${colClass} gap-6`}>
          {safe.cards.map((card, i) => (
            <div
              key={i}
              className={`${cardBaseClass}${needsTextText && !cardBaseClass.includes("text-text") ? " text-text" : ""} flex flex-col${safe.textAlign === "center" ? " text-center" : ""}`}
            >
              {card.icon && (
                <div className="mb-4">
                  <CardIcon name={card.icon} />
                </div>
              )}
              <RichText
                as={SubH}
                html={card.heading}
                className="text-xl font-semibold mb-3"
                data-prop={`cards.${i}.heading`}
              />
              <RichText
                as="p"
                html={card.body}
                className={`${dark && safe.cardStyle === "flat" ? "opacity-70" : "text-text-muted"} leading-relaxed mb-6 flex-1`}
                data-prop={`cards.${i}.body`}
              />
              {card.ctaLabel && card.ctaHref && (
                <a
                  href={card.ctaHref}
                  target={safe.ctaTarget}
                  rel={safe.ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
                  className={`inline-block font-semibold px-6 py-2.5 rounded-lg text-center transition-opacity ${ctaClasses(safe.ctaColor || "primary")}`}
                >
                  {card.ctaLabel}
                </a>
              )}
            </div>
          ))}
        </div>
    </SectionShell>
  );
}
